import type { GigyaAuthResponse, HardFilters } from '../shared/types/auth';

export class Session {
	/**
	 * Stores the current Gigya session data.
	 * Can be accessed via this.session after initialization.
	 */
	private session: Partial<GigyaAuthResponse> | null = null;

	/**
	 * Stores an in-flight promise for the Gigya session request.
	 * This prevents multiple concurrent API calls when getSession() is called multiple times
	 * before the first request completes (request deduplication pattern).
	 */
	private sessionPromise: Promise<GigyaAuthResponse | undefined> | null = null;

	/**
	 * Store the current user's ID, email, name, and other properties after initialization.
	 * Allows easy access without needing to decode the JWT each time.
	 */
	public userId: string | null = null;
	public email: string | null = null;
	public userName: string | null = null;
	public hardFilters: HardFilters | null = null;
	public onboardingComplete: boolean = false;

	/**
	 * Indicates whether the session has been initialized.
	 */
	public initialized: boolean = false;

	/**
	 * Constructor is empty to enforce async initialization via init() method.
	 */
	constructor() {}

	/**
	 * Initializes the session by fetching the current session data from Gigya.
	 * This method must be called and awaited before accessing session properties.
	 * We do this separately from the constructor because constructors cannot be async.
	 */
	async init(): Promise<void> {
		const session = await this.getSession();
		this.initialized = true;

		this.userId = session?.UID ?? null;
		this.email = session?.profile?.email ?? null;
		this.userName = session?.profile?.nickname ?? null;
		this.hardFilters = session?.data?.hardFilters ?? null;
		this.onboardingComplete = session?.data?.onboardingComplete ?? false;
	}

	/**
	 * Gets the current session, fetching from Gigya if needed or if JWT is expired.
	 * @return The current session or null if not available.
	 */
	private async getSession(): Promise<Partial<GigyaAuthResponse> | null> {
		// If we have a session and the token is still valid, return it
		if (this.session?.id_token && this.isJwtTokenValid(this.session.id_token)) {
			return this.session;
		}

		// Otherwise, fetch a new session from Gigya
		try {
			const gigyaSession = await this.checkGigyaSession();
			if (gigyaSession) {
				this.session = gigyaSession;
				return this.session;
			}
		} catch (error) {
			console.warn('Failed to fetch Gigya session:', error);
		}

		return null;
	}

	/**
	 * Gets the authentication token.
	 * @returns The JWT token or null if not available.
	 */
	async getAuthToken(): Promise<string | null> {
		const session = await this.getSession();
		return session?.id_token ?? null;
	}

	/**
	 * Checks if the user is currently logged in.
	 * @return True if logged in, false otherwise.
	 */
	async isLoggedIn(): Promise<boolean> {
		const session = await this.getSession();
		return session?.UID ? true : false;
	}

	/**
	 * Checks if the user is logged in by verifying session data stored in local storage.
	 * This is useful for scenarios where the session might be persisted in local storage
	 * and we want to avoid making an API call to Gigya.
	 *
	 * @return True if logged in based on local storage data, false otherwise.
	 */
	async isLoggedInWithLocalStorage(): Promise<boolean> {
		const userSession = localStorage.getItem('userSession');

		if (!userSession) {
			return false;
		}

		try {
			// On Local Storage session data is wrapped in an data object
			const sessionData = JSON.parse(userSession).data as GigyaAuthResponse;

			if (sessionData.UID) return true;
		} catch (error) {
			console.warn('Failed to parse user session from local storage:', error);
		}

		return false;
	}

	/**
	 * Returns the authentication header object for API requests.
	 * @return An object containing the x-sap-cdc-id-token header.
	 */
	async authHeader(): Promise<Record<string, string>> {
		const token = await this.getAuthToken();
		return {
			'x-sap-cdc-id-token': token ?? '',
		};
	}

	/**
	 * Checks the current Gigya session to ensure it's valid and retrieves the latest session data.
	 * @return A promise that resolves to the current GigyaAuthResponse or undefined if not available.
	 */
	private async checkGigyaSession(): Promise<GigyaAuthResponse | undefined> {
		if (typeof window === 'undefined' || !window.gigya) {
			return undefined;
		}

		// Reuse the same promise if a request is already in flight
		if (this.sessionPromise) {
			return this.sessionPromise;
		}

		this.sessionPromise = new Promise((resolve, reject) => {
			window.gigya.accounts.getAccountInfo({
				include: 'profile, data, subscriptions, loginIDs, preferences, id_token',
				callback: (result: GigyaAuthResponse) => {
					// Handle success response
					if (result.errorCode === '0' || Number(result.errorCode) === 0) {
						resolve(result);
					}

					// Handle unknown user response
					if (result.errorCode === '403005' || Number(result.errorCode) === 403005) {
						resolve(undefined);
					}

					// All other errors
					reject(
						new Error(
							`Gigya error ${result.errorCode}: ${result.errorDetails || result.errorMessage || 'Unknown error'}`
						)
					);
				},
			});
		});

		try {
			return await this.sessionPromise;
		} finally {
			this.sessionPromise = null;
		}
	}

	/**
	 * Validates a JWT token by checking its structure and expiration.
	 * @param token The JWT token to validate.
	 * @return True if the token is valid and not expired, false otherwise.
	 */
	private isJwtTokenValid(token: string): boolean {
		if (!token) {
			return false;
		}

		try {
			// Split the JWT into its three parts
			const parts = token.split('.');
			if (parts.length !== 3) {
				return false;
			}

			// Decode the payload (second part)
			const payload = JSON.parse(atob(parts[1]));

			// Check if the token has an expiration time
			if (!payload.exp) {
				return false;
			}

			// Check if the token is expired (exp is in seconds, Date.now() is in milliseconds)
			const currentTime = Math.floor(Date.now() / 1000);
			return payload.exp > currentTime;
		} catch (error) {
			console.warn('Failed to validate JWT token:', error);
			return false;
		}
	}

	/**
	 * Gets the decoded JWT token payload.
	 * @return The decoded JWT payload or null if invalid.
	 */
	async getJwtPayload(): Promise<Record<string, unknown> | null> {
		const token = await this.getAuthToken();

		if (!token) {
			return null;
		}

		try {
			const parts = token.split('.');
			if (parts.length !== 3) {
				return null;
			}

			return JSON.parse(atob(parts[1]));
		} catch (error) {
			console.warn('Failed to decode JWT token:', error);
			return null;
		}
	}

	/**
	 * Forces a refresh of the session from Gigya.
	 * @return The refreshed session or null if not available.
	 */
	async refreshSession(): Promise<Partial<GigyaAuthResponse> | null> {
		this.session = null;
		return await this.getSession();
	}
}
